import { AppConfig } from "./AppConfig.js";
import { ApiCache } from "./ApiCache.js";

// Único módulo que hace fetch. Los services de dominio arman el endpoint y el
// shape; los controllers nunca llaman fetch por su cuenta.
//
// No hay MockService: la API ya existe.

export class ApiService {
  static baseUrl = AppConfig.api.isLocal ? AppConfig.api.localUrl : AppConfig.api.baseUrl;
  static retryAttempts = AppConfig.api.retryAttempts;

  static async get(endpoint, token = null, options = {}) {
    return this.makeRequest("GET", this.baseUrl + "/" + endpoint, null, token, options);
  }

  static async post(endpoint, data, token = null, options = {}) {
    return this.makeRequest("POST", this.baseUrl + "/" + endpoint, data, token, options);
  }

  static async put(endpoint, data, token = null, options = {}) {
    return this.makeRequest("PUT", this.baseUrl + "/" + endpoint, data, token, options);
  }

  // Tras un POST que muta datos: ApiService.invalidate("sucursales")
  static invalidate(endpointContains = "", token = null) {
    ApiCache.invalidate({ urlContains: this.baseUrl + "/" + endpointContains, token });
  }

  // La API valida el JWT de LCApii reenviándolo; va como Bearer.
  static applyAuthHeader(headers, token) {
    headers["Authorization"] = "Bearer " + token;
  }

  static async makeRequest(method, url, data = null, token = null, options = {}) {
    const {
      cacheTiempo = false,
      cachePersistent = true,
      forceRefresh = false,
      dedupe = true,
    } = options;

    const cache = AppConfig.api.cacheEnabled ? cacheTiempo : false;
    const ttlMs = ApiCache.parseTTL(cache);
    const cacheKey = ApiCache.buildKey({ method, url, data, token });
    const canCache = ApiCache.canCache({ ttlMs, data });

    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    const requestPromise = (async () => {
      let lastError;
      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        try {
          const headers = {};
          if (token) this.applyAuthHeader(headers, token);
          let bodyToSend = data;
          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }
          const response = await fetch(url, { method, headers, body: bodyToSend || null });
          if (!response.ok) {
            // Un rechazo del servidor no se reintenta: reintentar un 401 o un 400
            // no cambia el resultado y solo demora el aviso al usuario.
            const error = new Error("HTTP error! status: " + response.status);
            error.status = response.status;
            if (response.status >= 400 && response.status < 500) throw Object.assign(error, { noReintentar: true });
            throw error;
          }
          const texto = await response.text();
          const json = texto ? JSON.parse(texto) : null;
          if (canCache) ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
          return json;
        } catch (error) {
          lastError = error;
          if (AppConfig.features?.debug) console.warn("Intento " + attempt + " fallo:", error);
          if (error.noReintentar || attempt === this.retryAttempts) break;
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
      console.error("API Error tras todos los reintentos:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }
}
